import { LitElement, html, nothing } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import typographyStyles from '@cof/fs-typography-core/lib/typography.lit.js';
import { trackAnalyticsEvent } from '@cof/refi-web-sdk';
import { HighlineService } from '@cof/auto-refinance-node-highline';
import { DebugService } from '../shared/services/debug.service.js';
import { style } from './personal.style.js';
import './customer-offer-banner/customer-offer-banner.component.js';
import './key-requirements/key-requirements.component.js';
import './steps/step-name/step-name.component.js';
import './steps/step-communication/step-communication.component.js';
import './steps/step-identity/step-identity.component.js';
import './steps/step-address/step-address.component.js';
import './steps/step-income/step-income.component.js';
import './steps/step-employment/step-employment.component.js';
import './steps/step-coborrower/step-coborrower.component.js';
import './refi-error/refi-error.component.js';
import './steps/step-iconic-income/step-iconic-income.component.js';
import './steps/step-address-v2/step-address-v2.component.js';

import { ApplicationService } from '../services/application.service.js';
import { globalStyle } from '../global-styles.style.js';
import { links } from '../styles/links.style.js';
import { REFI_PERSONAL_CONST } from '../shared/constants/app.constants.js';
import {
  showBackButtonEvent,
  stateChangedEvent,
} from './utils/host-app-events.js';
import { MatchingStateEnum } from './model/customer-offer.model.js';
import { ApiService } from '../core/services/api/api.service.js';
import { stratum } from '../../constants/stratum.js';

import './edit-card/edit-card.component.js';
import './steps/step-communication-v2/step-communication-v2.component.js';
import './steps/step-employment-v2/step-employment-v2.component.js';

class RefiPersonal extends LitElement {
  MFE_NAME = 'personal';

  constructor() {
    super();
    this.debug = new DebugService('RefiPersonal');
    this.loading = false;
    this.customerOffer = ApplicationService.CustomerOffer;
    this.matchingState = ApplicationService.MatchingState;
    this.application = {};
    this.iconicSpacing =
      ApplicationService.isIconicAndToggledOn || ApplicationService.isIconicV2;
    this.iOrWe = REFI_PERSONAL_CONST.PRONOUN.I;
    this.myOrOur = REFI_PERSONAL_CONST.POSSESSIVE_PRONOUN.MY;
    this.consentModalPersonText =
      REFI_PERSONAL_CONST.CONSENT_MODAL_PERSON_TEXT.SINGLE;
    this.consentAgree = false;

    // Skip identity step if offer was searched with cached taxId
    const skipIdentityStep = this.customerOffer
      ? this.customerOffer.cachedTaxId
      : false;

    this.v2Steps = ['communication', 'address', 'employment'];

    this.financialSteps = [
      'name',
      'communication',
      ...(skipIdentityStep ? [] : ['identity']),
      'address',
      'employment',
      'income',
    ];
    this.nonFinancialSteps = [
      'identity',
      'address',
      'employment',
      'income',
      'coborrower',
    ];
    this.iconicSteps = ['address', 'income'];
    this.coborrowerSteps = [
      'co-app-communication',
      'co-app-identity',
      'co-app-address',
      'co-app-employment',
      'co-app-income',
    ];
    this.currentSteps = [];
    this.currentStepIndex = 0;
    this.lighthouseSetup();
    this.hashChangeBinded = this.onHashChange.bind(this);
    this.initialSavePersonalInfo = false;
    this.missingPersonalInfo = true;
    this.checkIconicV2Steps();
  }

  static properties = {
    loading: { type: Boolean },
    customerOffer: { type: Object, reflect: true },
    matchingState: { type: String, reflect: true },
    application: { type: Object, reflect: true },
    steps: { type: Array, reflect: true },
    currentStepIndex: { type: Number, reflect: true },
    initialSavePersonalInfo: { type: Boolean },
    missingPersonalInfo: { type: Boolean },
    consentAgree: { type: Boolean, reflect: true },
    iconicSpacing: {
      type: Boolean,
      attribute: 'iconic-spacing',
      reflect: true,
    },
  };

  static styles = [style, globalStyle, links, typographyStyles];

  connectedCallback() {
    super.connectedCallback();
    this.application = ApplicationService.Application;
    this.setConsentModalTexts();
    this.debug.log('profileType', this.application.profileType);
    this._decisionCurrentSteps();
    this.application.isPrefilled =
      this.application.profileType === REFI_PERSONAL_CONST.FINANCIAL;
    window.addEventListener('hashchange', this.hashChangeBinded);
    stateChangedEvent(this, this.currentSteps, this.currentStepIndex, true);
    if (!HighlineService.publicKey) {
      HighlineService.urlPrefix = REFI_PERSONAL_CONST.HIGHLINE_URL_PREFIX();
      HighlineService.getPublicKey(
        REFI_PERSONAL_CONST.PREQUAL_BFF_HIGHLINE_PRODUCT_ID()
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('hashchange', this.hashChangeBinded);
  }

  setConsentModalTexts() {
    this.iOrWe =
      this.application.applicationType ===
      REFI_PERSONAL_CONST.APPLICATION_TYPE.JOINT
        ? REFI_PERSONAL_CONST.PRONOUN.WE
        : REFI_PERSONAL_CONST.PRONOUN.I;
    this.myOrOur =
      this.application.applicationType ===
      REFI_PERSONAL_CONST.APPLICATION_TYPE.JOINT
        ? REFI_PERSONAL_CONST.POSSESSIVE_PRONOUN.OUR
        : REFI_PERSONAL_CONST.POSSESSIVE_PRONOUN.MY;
    this.consentModalPersonText =
      this.application.applicationType ===
      REFI_PERSONAL_CONST.APPLICATION_TYPE.JOINT
        ? REFI_PERSONAL_CONST.CONSENT_MODAL_PERSON_TEXT.CO_APP
        : REFI_PERSONAL_CONST.CONSENT_MODAL_PERSON_TEXT.SINGLE;
    this.requestUpdate();
  }

  checkIconicV2Steps() {
    if (!REFI_PERSONAL_CONST.isProdEnv()) {
      this.v2Param = new URL(window.location.href)?.searchParams?.get(
        'iconic-v2'
      );
      this.experimentParam = new URL(window.location.href).searchParams.get(
        'experiment'
      );

      this.mockParam = new URL(window.location.href)?.searchParams?.get(
        'mock-app'
      );

      if (this.v2Param || this.experimentParam === 'iconic-prequal-v2-only') {
        this.debug.log('Setting up iconic v2 steps');
        ApplicationService.isIconicV2 = true;

        if (this.mockParam) {
          ApplicationService.setMockApplication();
        }

        this.currentSteps = this.v2Steps;
        this.currentStepIndex = 0;
        stateChangedEvent(this, this.currentSteps, this.currentStepIndex, true);
      }
    }
  }

  lighthouseSetup() {
    if (!REFI_PERSONAL_CONST.isProdEnv()) {
      this.testingParam = new URL(window.location.href)?.searchParams?.get(
        'testing'
      );
      const lighthouseConstKey = Object.keys(
        REFI_PERSONAL_CONST.LIGHTHOUSE_QUERY_PARAM
      ).find(
        (key) =>
          REFI_PERSONAL_CONST.LIGHTHOUSE_QUERY_PARAM[key].paramValue ===
          this.testingParam
      );
      if (lighthouseConstKey) {
        const { stepIndex } = {
          ...REFI_PERSONAL_CONST.LIGHTHOUSE_QUERY_PARAM[lighthouseConstKey],
        };
        ApplicationService.setMockApplication();
        this.currentSteps = (
          lighthouseConstKey === 'COBORROWER'
            ? this.nonFinancialSteps
            : this.financialSteps
        ).concat(this.coborrowerSteps);
        this.currentStepIndex = stepIndex;
        stateChangedEvent(this, this.currentSteps, this.currentStepIndex, true);
      }
    }
  }

  render() {
    return html`
      <div class="${this.iconicSpacing ? 'iconic-main' : 'main'}">
        <refi-track-pageview pagename="personal"></refi-track-pageview>
        <div ?hidden="${!this.customerOffer}">
          ${this.renderCustomerOfferBanner(
            this.currentSteps[this.currentStepIndex]
          )}
        </div>
        <div class="page-content">
          ${this.renderPage(this.currentSteps[this.currentStepIndex])}
        </div>
      </div>

      <fs-modal-dialog
        id="consent-modal"
        role="dialog"
        accessible-title="Let’s check your eligibility"
        mobile-layout="auto"
        aria-labelledby="consent-modal"
        size="small"
        dismissible
        @modal-dialog-open="${this.publishViewModal}"
        @modal-dialog-close="${this.publishCloseModal}"
      >
        <fs-heading
          class="modal-dialog-header"
          slot="modal-dialog-header"
          level="2"
          size="medium"
          variant="primary"
          >Let’s check your eligibility</fs-heading
        >
        <section
          class="fs-body-text fs-body-text--normal consent-modal-section"
          slot="modal-dialog-body"
        >
          <fs-body-text
            class="consent-modal-body-text"
            size="normal"
            variant="primary"
          >
            <span class="bold-text"
              >${this.consentModalPersonText} give Capital One permission to:
            </span>
          </fs-body-text>
          <fs-body-text size="normal" variant="primary">
            <ul class="consent-modal__list fs-body-text--normal">
              <li class="list_element">
                Get ${this.myOrOur} car info from state records
              </li>
              <li class="list_element">Confirm ${this.myOrOur} income</li>
              <li class="list_element">Pull ${this.myOrOur} credit reports</li>
            </ul>
          </fs-body-text>
          <fs-body-text size="normal" variant="primary">
            <span class="bold-text">(This won't impact your credit score)</span>
          </fs-body-text>
          <fs-body-text size="normal" variant="primary"
            >${this.iOrWe} agree to
            <a
              href="https://www.capitalone.com/privacy/"
              class="fs-link fs-link--primary"
              target="_blank"
              rel="noopener noreferrer"
              @click="${this.publishPrivacy}"
              >Capital One's Privacy Policies</a
            >
            and
            <a
              href="https://www.capitalone.com/bank/disclosures/eddn/"
              target="_blank"
              rel="noopener noreferrer"
              class="fs-link fs-link--primary"
              @click="${this.publishPaperless}"
              >Paperless Agreement</a
            >. ${this.iOrWe} understand ${this.iOrWe} must agree to both to
            apply for refinancing and that ${this.iOrWe} will receive all
            communications about ${this.myOrOur} refinance application and auto
            loan account electronically, not on paper.
          </fs-body-text>
        </section>
        <fs-modal-dialog-footer slot="modal-dialog-footer">
          <fs-button
            variant="primary"
            touched=""
            class="fs-button fs-button--primary modal-btn"
            @click="${this.clickedAgree}"
            ?loading="${this.consentAgree}"
          >
            ${this.iOrWe} agree</fs-button
          >
        </fs-modal-dialog-footer>
      </fs-modal-dialog>
    `;
  }

  clickedAgree() {
    this.consentAgree = true;
    ApplicationService.Application.hasConsent = true;
    stratum.publishTag(TagKey.PQ_CONSENT_MODAL_PQ_CONSENT_AGREEMENT);
    const consentModal = this.shadowRoot.querySelector('#consent-modal');
    consentModal.closeModalDialog();
    this.nextMfe();
  }

  publishViewModal() {
    this.consentAgree = false;
    stratum.publishTag(
      TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_CONSENT_MODAL_SHOWN
    );
  }

  publishCloseModal() {
    if (!this.consentAgree) {
      this.debug.log('Close modal tag.');
      stratum.publishTag(TagKey.PQ_CONSENT_MODAL_PQ_CONSENT_CLOSE);
    }
  }

  publishPrivacy() {
    stratum.publishTag(TagKey.PQ_CONSENT_MODAL_PQ_CONSENT_PRIVACY_POLICY);
  }

  publishPaperless() {
    stratum.publishTag(TagKey.PQ_CONSENT_MODAL_PQ_CONSENT_PAPERKESS_AGREEMENT);
  }

  renderCustomerOfferBanner(pageName) {
    let renderBanner = false;
    if (
      (pageName === 'name' &&
        this.matchingState === MatchingStateEnum.primarySso) ||
      (pageName === 'employment' &&
        this.matchingState === MatchingStateEnum.primaryNonSso) ||
      (pageName === 'co-app-employment' &&
        this.matchingState === MatchingStateEnum.coapplicant)
    ) {
      renderBanner = true;
    }
    return renderBanner
      ? html` <refi-customer-offer-banner
          customer-offer="${JSON.stringify(this.customerOffer)}"
          matching-state="${this.matchingState}"
        ></refi-customer-offer-banner>`
      : nothing;
  }

  renderPage(pageName) {
    // if we fail to find a page, go back to the first page.
    this.debug.log('renderPage called with: ', pageName);
    if (pageName === undefined) {
      this.debug.log('page name undefined: ', pageName);
      stateChangedEvent(this, this.currentSteps, 0);
      return nothing;
    }

    const isCoApp = pageName.includes('co-app');
    if (isCoApp && ApplicationService.Application.applicationType !== 'JOINT') {
      this.nextStep();
      return nothing;
    }
    this.shouldShowBackButton(pageName);
    switch (pageName) {
      case 'name':
        return html` <refi-step-name
          customer-offer="${JSON.stringify(this.customerOffer)}"
          @next-step="${() => {
            this._decisionCurrentSteps();
            this.nextStep();
          }}"
        ></refi-step-name>`;
      case 'communication':
      case 'co-app-communication':
        return html` ${ApplicationService.isIconicV2
          ? html`<refi-step-communication-v2
              @next-step="${this.nextStep}"
            ></refi-step-communication-v2>`
          : html` <refi-step-communication
              @next-step="${this.nextStep}"
              ?is-prefill="${this.application.isPrefilled}"
              ?is-primary="${!isCoApp}"
            ></refi-step-communication>`}`;
      case 'identity':
      case 'co-app-identity':
        return html` <refi-step-identity
          @next-step="${this.nextStep}"
          ?is-prefill="${this.application.isPrefilled}"
          ?is-primary="${!isCoApp}"
        ></refi-step-identity>`;
      case 'address':
      case 'co-app-address':
        return html` ${ApplicationService.isIconicV2
          ? html`<refi-step-address-v2
              @next-step="${this.nextStep}"
              ?is-prefill="${this.application.isPrefilled}"
            ></refi-step-address-v2>`
          : html`<refi-step-address
              @next-step="${this.nextStep}"
              ?is-prefill="${this.application.isPrefilled}"
              ?is-primary="${!isCoApp}"
              ?is-iconic="${ApplicationService.isIconicAndToggledOn}"
            ></refi-step-address>`}`;
      case 'employment':
      case 'co-app-employment':
        return html` ${ApplicationService.isIconicV2
          ? html`<refi-step-employment-v2
              @next-step="${this.nextStep}"
              ?is-prefill="${this.application.isPrefilled}"
            ></refi-step-employment-v2>`
          : html` <refi-step-employment
              @next-step="${this.nextStep}"
              ?is-prefill="${this.application.isPrefilled}"
              ?is-primary="${!isCoApp}"
            ></refi-step-employment>`}`;
      case 'income':
      case 'co-app-income':
        return html` ${ApplicationService.isIconicAndToggledOn
          ? html`
              <refi-step-iconic-income
                ?is-prefill="${this.application.isPrefilled}"
                ?is-primary="${!isCoApp}"
                @next-step="${this.nextStep}"
              ></refi-step-iconic-income>
            `
          : html` <refi-step-income
              @next-step="${this.nextStep}"
              ?is-prefill="${this.application.isPrefilled}"
              ?is-primary="${!isCoApp}"
            ></refi-step-income>`}`;
      case 'coborrower':
        return html` <refi-step-coborrower
          @next-step="${() => {
            this._decisionCurrentSteps();
            this.nextStep();
          }}"
          ?is-prefill="${this.application.isPrefilled}"
        ></refi-step-coborrower>`;
      default:
        return nothing;
    }
  }

  shouldShowBackButton(pageName) {
    if (
      !ApplicationService.isUpfrontOffers &&
      ((pageName === 'identity' &&
        this.application.profileType === REFI_PERSONAL_CONST.NON_FINANCIAL) ||
        pageName === 'name')
    ) {
      showBackButtonEvent(this, false);
    } else {
      showBackButtonEvent(this, true);
    }
  }

  firstUpdated() {
    if (!this._getCurrentHash() || !this.currentSteps[this._getCurrentHash()]) {
      this.debug.log(
        'first updated, first has undefined',
        this._getCurrentHash()
      );
      trackAnalyticsEvent(
        `loaded step ${this.currentSteps[this.currentStepIndex]}`
      );
      trackAnalyticsEvent(`profile ${this.application.profileType}`);
      stratum.publishTag(TagKey.PERSONAL_CUSTOMER_PROFILE_TYPE, {
        replacements: { value: this.application.profileType },
      });
      if (!this.testingParam) {
        stateChangedEvent(this, this.currentSteps, 0);
      }
    }
    stratum.publishTag(TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_PERSONAL);
    trackAnalyticsEvent(TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_PERSONAL);
    stratum.publishTag(TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_MODPREQUAL);
    trackAnalyticsEvent(TagKey.PAGE_VIEW_COAF_REFI_PREQUALIFY_MODPREQUAL);
  }

  nextStep() {
    this.debug.log('next step even triggered');
    stratum.publishTag(TagKey.PREQUALIFY_NEXT_PAGE_CTA, {
      replacements: { value: this.currentSteps[this.currentStepIndex] },
    });
    trackAnalyticsEvent(`step ${this.currentSteps[this.currentStepIndex]}`);
    this.application = ApplicationService.Application;
    this.debug.log('application', this.application);
    this.loading = true;
    const isLastStep = this.currentStepIndex + 1 === this.currentSteps.length;

    if (
      (!this.initialSavePersonalInfo || isLastStep) &&
      this.application.primaryApplicant.dateOfBirth
    ) {
      this.savePersonalInfo();
    }

    if (isLastStep) {
      this.setConsentModalTexts();
      if (ApplicationService.isIconicV2) {
        // show modal
        const consentModal = this.shadowRoot.querySelector('#consent-modal');
        consentModal.openModalDialog();
      } else {
        this.nextMfe();
      }
    } else {
      this.debug.log('next-step', this.currentStepIndex + 1);
      stateChangedEvent(this, this.currentSteps, this.currentStepIndex + 1);
    }
  }

  nextMfe() {
    this.debug.log('Sending next-mfe event to host app');
    this.dispatchEvent(
      new CustomEvent('host-app-update-application', {
        bubbles: true,
        composed: true,
        detail: {
          data: { application: this.application },
        },
      })
    );
    this.dispatchEvent(
      new CustomEvent('host-app-event', {
        bubbles: true,
        composed: true,
        detail: {
          actionName: 'next-mfe',
          data: { application: this.application },
        },
      })
    );
  }

  onHashChange() {
    const { hash } = window.location;
    const [, poppedMfeName, poppedState] = hash.split('/');
    this.debug.log('onHashChange', poppedState);

    if (
      poppedMfeName === this.MFE_NAME &&
      poppedState !== this.currentSteps[this.currentStepIndex] &&
      !this.testingParam
    ) {
      this.debug.log('on hash change, hash not current step: ', poppedState);
      this.currentStepIndex = this.currentSteps.indexOf(poppedState);
      document.body.scrollIntoView(true);
      stateChangedEvent(this, this.currentSteps, this.currentStepIndex);
    }
  }

  _getCurrentHash() {
    const { hash } = window.location;
    const [, , poppedState] = hash.split('/');
    return poppedState;
  }

  _decisionCurrentSteps() {
    if (!this.testingParam && !this.v2Param) {
      let steps = ApplicationService.isIconicAndToggledOn
        ? this.iconicSteps
        : this.application.profileType === REFI_PERSONAL_CONST.FINANCIAL
        ? this.financialSteps
        : this.nonFinancialSteps;
      if (this.application.applicationType === 'JOINT') {
        stratum.publishTag(TagKey.PERSONAL_NAME_CO_APPLICANT_ADDED);
        steps = steps.concat(this.coborrowerSteps);
      }
      if (ApplicationService.isIconicV2) {
        steps = this.v2Steps;
      }
      this.currentSteps = steps;
    }
  }

  savePersonalInfo() {
    const personalInfo = this.populatePersonalInfoObject();

    this.debug.log('save personal info with', personalInfo);
    ApiService.post(REFI_PERSONAL_CONST.SAVE_PERSONAL_INFO_URL, personalInfo)
      .then(() => {
        this.initialSavePersonalInfo = true;
        trackAnalyticsEvent('refileads_login');
        stratum.publishTag(TagKey.REFI_LEADS_PERSONAL_LEADS_SAVE);
      })
      .catch((err) => {
        this.debug.log('save-personal-info call failed', err);
      });
  }

  populatePersonalInfoObject() {
    const personalInfo = { customerList: [] };
    if (this.application.primaryApplicant) {
      const applicant = this.application.primaryApplicant;
      const employmentStatus =
        applicant.employmentStatus?.toUpperCase() || null;
      personalInfo.customerList[0] = {
        applicantType: 'BORROWER',
        firstName: applicant.firstName,
        lastName: applicant.lastName,
        dateOfBirth: applicant.dateOfBirth,
        phoneNumber: applicant.phoneNumber,
        employmentStatus,
        employerName: applicant.employmentInfo.employerName,
        incomeAmount: applicant.incomeAmount,
        lastFourSsn: applicant.ssn.substring(applicant.ssn.length - 4),
        emailAddress: applicant.emailAddress,
        address: applicant.currentAddress.street
          ? {
              addressLine1: applicant.currentAddress.street,
              addressLine2: applicant.currentAddress.aptNumber,
              city: applicant.currentAddress.city,
              stateCode: applicant.currentAddress.stateCode,
              postalCode: applicant.currentAddress.zipCode,
            }
          : null,
      };
      if (applicant.incomeAmount <= 0) {
        delete personalInfo.customerList[0].incomeAmount;
      }
      personalInfo.customerList[0] = this.removeNullKeys(
        personalInfo.customerList[0]
      );
    }
    if (
      this.application.applicationType.toLowerCase() === 'joint' &&
      this.application.coApplicant !== undefined &&
      this.application.coApplicant.dateOfBirth &&
      this.application.coApplicant.emailAddress &&
      this.application.coApplicant.lastName
    ) {
      const { coApplicant } = this.application;
      const employmentStatus =
        coApplicant.employmentStatus.toUpperCase() || null;
      personalInfo.customerList[1] = {
        applicantType: 'COBORROWER',
        firstName: coApplicant.firstName,
        lastName: coApplicant.lastName,
        dateOfBirth: coApplicant.dateOfBirth,
        phoneNumber: coApplicant.phoneNumber,
        employmentStatus,
        employerName: coApplicant.employmentInfo.employerName,
        lastFourSsn: coApplicant.ssn.substring(coApplicant.ssn.length - 4),
        emailAddress: coApplicant.emailAddress,
        address: coApplicant.currentAddress.street
          ? {
              addressLine1: coApplicant.currentAddress.street,
              addressLine2: coApplicant.currentAddress.aptNumber,
              city: coApplicant.currentAddress.city,
              stateCode: coApplicant.currentAddress.stateCode,
              postalCode: coApplicant.currentAddress.zipCode,
              ownerStatus: coApplicant.currentAddress.ownerStatus,
              monthlyPayment: coApplicant.currentAddress.monthlyPayment,
            }
          : null,
      };
      if (coApplicant.incomeAmount <= 0) {
        delete personalInfo.customerList[1].incomeAmount;
      }
      personalInfo.customerList[1] = this.removeNullKeys(
        personalInfo.customerList[1]
      );
    }
    if (this.application.promoCode) {
      personalInfo.offerCode = this.application.promoCode;
    }

    return personalInfo;
  }

  removeNullKeys(applicant) {
    return Object.keys(applicant).reduce((result, key) => {
      if (applicant[key] !== null && applicant[key] !== '') {
        result[key] = applicant[key];
      }
      return result;
    }, {});
  }
}

customElements.define('refi-personal', RefiPersonal);
